import * as React from 'react';
import { Button, Typography, Box, Menu, MenuItem, ListSubheader, Link } from '@unity/cloud-ui';
import { CaretDown, CaretUp } from '@phosphor-icons/react';

export interface HeaderMenuLinksDesktopProps {
  label: string;
  href?: string;
  items?: {
    label: string;
    href?: string;
  }[];
  container?: HTMLDivElement | null;
}

export const HeaderMenuLinksDesktop = ({ label, href, container, items }: HeaderMenuLinksDesktopProps) => {
  const buttonId = `header-section-button-${label.toLocaleLowerCase()}`;
  const menuId = `header-section-menu-${label.toLocaleLowerCase()}`;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const timeout = React.useRef<NodeJS.Timeout | undefined>(undefined);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    if (timeout) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(() => {
      setAnchorEl(null);
    }, 0);
  };

  const handleMenuLeave = () => {
    setAnchorEl(null);
  };

  const handleMenuEnter = () => {
    if (timeout) {
      clearTimeout(timeout.current);
    }
  };

  if (!items) {
    return (
      <Button color="default" id={buttonId} component={Link} href={href}>
        <Typography
          variant="h2"
          fontSize="0.875rem"
          fontWeight={500}
          lineHeight="1.75rem"
          letterSpacing={0}
          color="text.primary"
        >
          {label}
        </Typography>
      </Button>
    );
  }

  const sections = React.useMemo(() => {
    return items.reduce<{ header?: { label: string }; items: { label: string; href?: string }[] }[]>((acc, item) => {
      if (!item.href) {
        acc.push({
          header: item,
          items: [],
        });
        return acc;
      }

      if (!acc[acc.length - 1]) {
        acc.push({ items: [] });
      }

      acc[acc.length - 1].items.push(item);

      return acc;
    }, []);
  }, [items]);

  const renderSection = (section, i) => {
    const children: React.JSX.Element[] = [];
    if (section.header) {
      if (section.header.label === 'PLACEHOLDER') {
        return <div key={`placeholder_${i}`}>&nbsp;</div>;
      }

      children.push(
        <ListSubheader key={section.header.label}>
          <Typography variant="h6" color="text.secondary">
            {section.header.label}
          </Typography>
        </ListSubheader>,
      );
    }

    section.items.forEach((item) => {
      children.push(
        <MenuItem
          key={item.label}
          href={item.href}
          component={Link}
          onClick={handleClose}
          external={item.href.startsWith('http')}
          sx={{
            minWidth: '145px',
            display: 'flex',
            justifyContent: 'space-between',
            '&:hover': { color: 'inherit' },
          }}
        >
          {item.label}
        </MenuItem>,
      );
    });

    return children;
  };

  return (
    <>
      <Button
        color="default"
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpen}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        sx={{ zIndex: ({ zIndex }) => zIndex.modal }}
        suppressHydrationWarning
      >
        <Typography variant="h2" fontSize="0.875rem" fontWeight={500} lineHeight="1.75rem" letterSpacing={0}>
          {label}
        </Typography>
        <Box
          display="flex"
          sx={{
            marginLeft: 0.5,
          }}
        >
          {open ? <CaretUp /> : <CaretDown />}
        </Box>
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        disableScrollLock
        container={container}
        disableRestoreFocus
        closeAfterTransition
        disableAutoFocusItem
        open={open}
        onClose={handleClose}
        sx={{ zIndex: ({ zIndex }) => zIndex.modal - 1 }}
        slotProps={{
          paper: {
            onMouseLeave: handleMenuLeave,
            onMouseEnter: handleMenuEnter,
          },
          list: {
            'aria-labelledby': buttonId,
            ...(sections.length > 3 && {
              sx: {
                width: '670px',
                height: '325px',
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'wrap',
              },
            }),
          },
        }}
      >
        {sections.map(renderSection)}
      </Menu>
    </>
  );
};
